import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import themesConfig from 'app/fuse-configs/themesConfig'
import { Fade, IconButton, makeStyles, Typography } from '@material-ui/core'
import AddPhotoAlternateOutlinedIcon from '@material-ui/icons/AddPhotoAlternateOutlined'

import effects from 'app/effects/effects'

import MessageAlert from 'app/components/MessageAlert'

import PhotoGrid from './PhotoGrid'
import PhotoGalleryEmpty from './PhotoGalleryEmpty'
import PhotoUploadDialog from './PhotoUploadDialog'
import { messageAlertError } from 'commons/error'
// import MessagespringLoading from 'app/componentV4/Loader/MessagespringLoading'

const useStyles = makeStyles((theme) => ({
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.primary,
  },
  subtitle: {
    fontSize: 12,
    color: theme.colors['primary-300'],
  },
  addButton: {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
}))

export default function PhotoGallery() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const [images, setImages] = useState([])
  const [imagesGrid, setImagesGrid] = useState([])
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)

  const fetchGallery = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.profileInfo.getGallery()
      return response.data
    },
    onSuccess: (data) => {
      setImages(data)
      setImagesGrid(data.slice().reverse())
    },
    onError: (error) => {
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(error, formatMessage),
        }),
      )
    },
  })

  useEffect(() => {
    fetchGallery.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddImage = () => {
    setIsUploadDialogOpen(true)
  }

  const handleCloseDialog = useCallback(() => {
    setIsUploadDialogOpen(false)
    fetchGallery.mutate()
  }, [fetchGallery])

  const hasImages = images.length > 0

  return (
    <>
      <div className="bg-white p-10 border-1 border-primary-350 rounded-5 rounded-tr-5 overflow-x-auto">
        <div className="flex justify-between items-center">
          <div className={classes.titleSection}>
            <Typography className={classes.title}>
              {formatMessage(
                { id: 'profile.info.photo' },
                { defaultValue: 'Photo Gallery' },
              )}
            </Typography>
            <Typography className={classes.subtitle}>
              (
              {formatMessage(
                { id: 'profile.info.max.size' },
                { defaultValue: 'Max 10MB per image' },
              )}
              )
            </Typography>
          </div>

          {hasImages && (
            <Fade in={hasImages} timeout={300}>
              <IconButton
                onClick={handleAddImage}
                className={classes.addButton}
                aria-label="Add photos"
              >
                <AddPhotoAlternateOutlinedIcon
                  fontSize="small"
                  style={{ color: themesConfig.default.colors.primary }}
                />
              </IconButton>
            </Fade>
          )}
        </div>

        {!hasImages ? (
          <PhotoGalleryEmpty
            onAddImage={handleAddImage}
            isLoading={fetchGallery.isPending}
          />
        ) : (
          <PhotoGrid
            photos={imagesGrid}
            maxVisible={12}
            onAddMore={handleAddImage}
          />
        )}
      </div>
      <PhotoUploadDialog
        images={images}
        isOpen={isUploadDialogOpen}
        onClose={handleCloseDialog}
      />
    </>
  )
}
